import { ApiError, authHeaders, requestJson } from '../api/http';
import { ListenerCaptureTarget } from './captureClient';

export interface ComponentReview {
  supported: true;
  kind: 'intro' | 'goodnight';
  recording_version_id: string;
  audio_sha256: string;
  status: 'pending' | 'approved' | 'withdrawn';
}
export interface MessageRejected {
  accepted: false;
  storage_confirmed: false;
  rejection_reasons: string[];
}
export const sharedReviewEnabled = process.env.REACT_APP_SHARED_HOUSEHOLD_NARRATION === 'true';
const base = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';
function path(target: ListenerCaptureTarget): string {
  if (!target.voiceId || !target.listenerId) throw new Error('Listener unavailable');
  return `${base}/voice/${encodeURIComponent(target.voiceId)}/listeners/${encodeURIComponent(target.listenerId)}/messages`;
}
function checked(value: ComponentReview | null, kind: string): ComponentReview | null {
  if (value === null) return null;
  if (!value || value.supported !== true || value.kind !== kind
    || !/^[0-9a-f-]{36}$/.test(value.recording_version_id)
    || !/^[0-9a-f]{64}$/.test(value.audio_sha256)
    || !['pending', 'approved', 'withdrawn'].includes(value.status)) throw new Error('Saved message could not be verified');
  return value;
}
export async function messageStatus(target: ListenerCaptureTarget) {
  const value = await requestJson<{component_review_policy: string; messages: Record<string, ComponentReview | null>}>(path(target), { headers: authHeaders(), cache: 'no-store' });
  if (value.component_review_policy !== 'component-audio-v1') throw new Error('Message review unavailable');
  return { intro: checked(value.messages.intro, 'intro'), goodnight: checked(value.messages.goodnight, 'goodnight') };
}
export async function saveMessage(target: ListenerCaptureTarget, kind: 'intro' | 'goodnight', blob: Blob, requestId: string) {
  const form = new FormData(); form.append('request_id', requestId); form.append('file', blob, `${kind}.wav`);
  let result: {component_review_policy: string; component_review?: ComponentReview; accepted?: boolean; storage_confirmed?: boolean; rejection_reasons?: string[]};
  try {
    result = await requestJson<typeof result>(`${path(target)}/${kind}`, { method: 'POST', headers: authHeaders(), body: form });
  } catch (error) {
    // Older API revisions reject audio with 422. Retrying identical bytes
    // cannot fix that rejection; keep transport failures retryable separately.
    if (error instanceof ApiError && error.status === 422) {
      return {accepted: false, storage_confirmed: false,
        rejection_reasons: ['Recording was not accepted. Please record again.']} as MessageRejected;
    }
    throw error;
  }
  if (result.component_review_policy !== 'component-audio-v1') throw new Error('Message review unavailable');
  if (result.accepted === false && result.storage_confirmed === false && !result.component_review) {
    return {accepted: false, storage_confirmed: false,
      rejection_reasons: Array.isArray(result.rejection_reasons) ? result.rejection_reasons.filter(x => typeof x === 'string') : []} as MessageRejected;
  }
  const receipt = checked(result.component_review ?? null, kind);
  if (!receipt) throw new Error('Saved message could not be verified');
  return receipt;
}
export async function messageAudio(target: ListenerCaptureTarget, receipt: ComponentReview): Promise<Blob> {
  checked(receipt, receipt.kind);
  const response = await fetch(`${path(target)}/${receipt.kind}/${receipt.recording_version_id}/audio`, {headers: authHeaders(), cache: 'no-store'});
  if (!response.ok) throw new Error('Saved message unavailable');
  const bytes = await response.arrayBuffer();
  const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), (b) => b.toString(16).padStart(2, '0')).join('');
  if (hash !== receipt.audio_sha256) throw new Error('Saved message changed');
  return new Blob([bytes], {type: 'audio/wav'});
}
export async function approveMessage(target: ListenerCaptureTarget, receipt: ComponentReview, requestId: string) {
  const result = await requestJson<{component_review_policy: string; component_review: ComponentReview}>(`${path(target)}/${receipt.kind}/review`, {
    method: 'POST', headers: {...authHeaders(), 'Content-Type':'application/json'},
    body: JSON.stringify({recording_version_id: receipt.recording_version_id, audio_sha256: receipt.audio_sha256, request_id: requestId, reviewed: true}),
  });
  if (result.component_review_policy !== 'component-audio-v1') throw new Error('Message review unavailable');
  const saved = checked(result.component_review, receipt.kind);
  if (!saved || saved.status !== 'approved' || saved.recording_version_id !== receipt.recording_version_id || saved.audio_sha256 !== receipt.audio_sha256) throw new Error('Review could not be confirmed');
  return saved;
}
